import { useState, useEffect, useMemo, useRef } from "react";
import { MapPin, Tag, Store } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { BUSINESSES, IMAGES } from "@/data/content";
import { api, asset } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { OwnershipPolicy, SalesGuide } from "@/components/BusinessPolicy";
import { BusinessAlerts } from "@/components/BusinessAlerts";
import { BusinessRules } from "@/components/BusinessRules";
import { HowToApply } from "@/components/BusinessRules";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { createTileMap, pctToLatLng, pinIcon, clusterIcon, TEAL } from "@/lib/mapTiles";

const TABS = [{ id: "map", label: "Map" }, { id: "rules", label: "Business Rules" }, { id: "ownership", label: "Ownership & Family" }, { id: "sales", label: "Sales Guide" }, { id: "alerts", label: "Business Alerts" }];

const StatusBadge = ({ b }) =>
  b.for_sale ? (
    <span className="font-mono text-[10px] uppercase tracking-wider bg-teal text-obsidian px-2 py-0.5 whitespace-nowrap">
      For Sale{b.price ? ` — ${b.price}` : ""}
    </span>
  ) : (
    <span className="font-mono text-[10px] uppercase tracking-wider border border-[#3a2f27] text-[#8F877C] px-2 py-0.5">Owned</span>
  );

const CatButton = ({ id, label, count, activeCat, onSelect, testid }) => {
  const on = activeCat === id;
  return (
    <button onClick={() => onSelect(id)} data-testid={testid}
      className={`flex items-center justify-between w-full px-4 py-2.5 text-left font-feature text-xs uppercase tracking-wider border-l-2 transition-colors ${on ? "border-teal bg-surface text-teal" : "border-transparent text-[#8F877C] hover:text-bone hover:bg-surface/40"}`}>
      <span>{label}</span>
      <span className={`font-mono text-[10px] ${on ? "text-teal" : "text-[#5c554c]"}`}>{count}</span>
    </button>
  );
};

// ---- Leaflet tiled map (RedM live-map tile pyramid, zoom 1–6) ----
const TileMap = ({ businesses, onSelect }) => {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;

  useEffect(() => {
    if (mapRef.current || !divRef.current) return;
    const { map } = createTileMap(divRef.current, { zoomControl: true });
    const cluster = L.markerClusterGroup({
      maxClusterRadius: 40,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 6,   // once zoomed into a town, show every pin at its exact spot
      iconCreateFunction: clusterIcon,
    });
    map.addLayer(cluster);
    clusterRef.current = cluster;
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster) return;
    cluster.clearLayers();
    businesses.forEach((b) => {
      const id = b.id || b.name;
      const marker = L.marker(pctToLatLng(map, b.x, b.y), { icon: pinIcon(b.for_sale), forSale: !!b.for_sale, bizId: id });
      const status = b.for_sale
        ? `<span style="color:${TEAL};font-weight:700">FOR SALE${b.price ? ` — ${b.price}` : ""}</span>`
        : `<span style="color:#8F877C">Owned</span>`;
      marker.bindPopup(
        `<div style="font-family:ui-monospace,monospace"><div style="font-size:14px;color:#e8e0d4;margin-bottom:2px">${b.name}</div><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#8F877C;margin-bottom:6px">${b.type || ""}${b.town ? ` · ${b.town}` : ""}</div>${status}</div>`,
        { closeButton: false }
      );
      marker.on("click", () => selectRef.current && selectRef.current(id));
      cluster.addLayer(marker);
    });
  }, [businesses]);

  return <div ref={divRef} data-testid="business-map" className="w-full aspect-[2000/1483] bg-[#141210]" style={{ zIndex: 0 }} />;
};

const BusinessMap = () => {
  const [list, setList] = useState(BUSINESSES);
  const [active, setActive] = useState(null);
  const [cat, setCat] = useState("all");
  const [saleOnly, setSaleOnly] = useState(false);

  useEffect(() => {
    api.get("/businesses").then(({ data }) => { if (data?.length) setList(data); }).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const m = {};
    list.forEach((b) => { const t = b.type || "Other"; m[t] = (m[t] || 0) + 1; });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [list]);

  const forSaleCount = useMemo(() => list.filter((b) => b.for_sale).length, [list]);

  const filtered = useMemo(
    () => list.filter((b) => (cat === "all" || (b.type || "Other") === cat) && (!saleOnly || b.for_sale)),
    [list, cat, saleOnly]
  );

  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach((b) => { const t = b.type || "Other"; (m[t] = m[t] || []).push(b); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const [openTypes, setOpenTypes] = useState([]);
  useEffect(() => { if (cat !== "all") setOpenTypes([cat]); }, [cat]);

  const activeBiz = filtered.find((b) => (b.id || b.name) === active);

  const selectCat = (id) => { setCat(id); setActive(null); };

  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-10 py-14 grid gap-8 lg:grid-cols-[240px_1fr]">
      {/* Sidebar */}
      <aside className="space-y-6" data-testid="biz-sidebar">
        <div>
          <p className="flex items-center gap-2 font-feature text-xs uppercase tracking-[0.25em] text-teal mb-3"><Store className="h-4 w-4" /> Trades ({list.length})</p>
          <div className="border border-[#2A201A] bg-surface/20 divide-y divide-[#2A201A]/60">
            <CatButton id="all" label="All Businesses" count={list.length} activeCat={cat} onSelect={selectCat} testid="biz-cat-all" />
            {categories.map(([t, c]) => (
              <CatButton key={t} id={t} label={t} count={c} activeCat={cat} onSelect={selectCat} testid={`biz-cat-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            ))}
          </div>
        </div>

        <button onClick={() => { setSaleOnly((s) => !s); setActive(null); }} data-testid="biz-filter-forsale"
          className={`flex items-center justify-between w-full px-4 py-3 font-feature text-xs uppercase tracking-wider border transition-colors ${saleOnly ? "border-teal bg-teal text-obsidian" : "border-[#2A201A] text-bone hover:border-teal/50"}`}>
          <span className="flex items-center gap-2"><Tag className="h-4 w-4" /> For Sale only</span>
          <span className="font-mono text-[10px]">{forSaleCount}</span>
        </button>

        <div className="space-y-2 pt-2 border-t border-[#2A201A]">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#5c554c] mb-2">Legend</p>
          <div className="flex items-center gap-2 text-xs text-[#8F877C]"><MapPin className="h-4 w-4 text-teal" fill="rgb(var(--brand-teal))" /> Up for grabs</div>
          <div className="flex items-center gap-2 text-xs text-[#8F877C]"><MapPin className="h-4 w-4 text-[#8a1f1f]" fill="#8a1f1f" /> Owned / occupied</div>
        </div>
      </aside>

      {/* Main */}
      <div className="space-y-8">
        {/* Map */}
        <div className="relative border border-[#2A201A] overflow-hidden">
          <TileMap businesses={filtered} onSelect={setActive} />
          <span className="absolute bottom-3 left-3 z-[500] font-mono text-[10px] uppercase tracking-wider text-obsidian/70 bg-bone/70 px-2 py-1 pointer-events-none">Scroll to zoom · drag to pan · click a cluster to expand</span>
        </div>

        {/* Ledger — grouped by trade */}
        <div>
          <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-4">
            Town Ledger{saleOnly ? " · For Sale" : ""} <span className="text-[#5c554c]">({filtered.length})</span>
          </p>
          {grouped.length === 0 ? (
            <p className="font-mono text-xs uppercase tracking-widest text-[#5c554c] py-6">No businesses match this filter.</p>
          ) : (
            <Accordion type="multiple" value={openTypes} onValueChange={setOpenTypes} className="space-y-2">
              {grouped.map(([type, items]) => {
                const sale = items.filter((b) => b.for_sale).length;
                return (
                  <AccordionItem key={type} value={type} data-testid={`ledger-group-${type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="border border-[#2A201A] bg-surface/20 px-0">
                    <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-surface/40 [&>svg]:text-teal">
                      <span className="flex items-center gap-3 font-feature text-xs uppercase tracking-[0.2em] text-bone">
                        {type}
                        <span className="font-mono text-[10px] text-[#5c554c]">{items.length}</span>
                        {sale > 0 && <span className="font-mono text-[10px] text-teal">· {sale} for sale</span>}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3 pt-0">
                      <div className="grid gap-2 sm:grid-cols-2">
                        {items.map((b) => {
                          const id = b.id || b.name;
                          const on = active === id;
                          return (
                            <button key={id} data-testid={`business-item-${id}`} onMouseEnter={() => setActive(id)} onClick={() => setActive(id)}
                              className={`w-full text-left border p-4 transition-all ${on ? "border-teal/50 bg-surface aurora-glow sm:col-span-2" : "border-[#2A201A] bg-obsidian/40 hover:border-[#3a2f27]"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="font-display text-lg font-light">{b.name}</h3>
                                  {b.town && <span className="font-mono text-[10px] uppercase tracking-wider text-[#8F877C]">{b.town}</span>}
                                </div>
                                <StatusBadge b={b} />
                              </div>
                              {on && (
                                <div className="mt-4 flex flex-col sm:flex-row gap-5">
                                  {b.image && <img src={asset(b.image)} alt={b.name} className="h-40 w-full sm:w-56 object-cover border border-[#2A201A] shrink-0" data-testid={`business-photo-${id}`} />}
                                  <div className="space-y-3">
                                    {b.body && <p className="text-sm text-[#b8b0a4] leading-relaxed">{b.body}</p>}
                                    {b.for_sale && (
                                      <p className="font-mono text-xs text-teal">Listing price: <span className="text-bone">{b.price || "Enquire with staff"}</span></p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>

        {/* How to Apply — also surfaced on the Map tab under the ledger */}
        <HowToApply className="border-t border-[#2A201A] pt-12" />
      </div>

      {activeBiz && <span className="hidden" data-testid="active-biz-name">{activeBiz.name}</span>}
    </section>
  );
};

export default function Businesses() {
  useTitle("Businesses | Aurora Springs RP | RedM Roleplay", "Explore player-owned businesses, the interactive county map, business rules and how to apply for your own venture in Aurora Springs RP.");
  const { img } = useSiteConfig();
  const [tab, setTab] = useState("map");
  return (
    <>
      <PageHeader overline="The town of Aurora Springs" title="Businesses" image={img("businesses", IMAGES.townRide)}
        sub="Every door in town belongs to someone. Explore the map, find what's up for grabs, or read the ownership and sales policies before you set up shop." />

      <div className="mx-auto max-w-7xl px-6 sm:px-10 pt-10">
        <div className="flex flex-wrap gap-2 border-b border-[#2A201A]">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`biz-tab-${t.id}`}
              className={`px-5 py-3 font-feature text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === t.id ? "border-teal text-teal" : "border-transparent text-[#8F877C] hover:text-bone"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "ownership" && <section className="mx-auto max-w-4xl px-6 sm:px-10 py-14">
        <h2 className="font-display text-3xl sm:text-4xl font-light text-bone mb-2">Business Ownership &amp; Family Structure</h2>
        <p className="font-body text-[15px] text-[#8F877C] mb-10">Policy for all characters and player associations within the County.</p>
        <OwnershipPolicy />
      </section>}

      {tab === "sales" && <section className="mx-auto max-w-5xl px-6 sm:px-10 py-14">
        <h2 className="font-display text-3xl sm:text-4xl font-light text-bone mb-2">Business Sales Guide</h2>
        <p className="font-body text-[15px] text-[#8F877C] mb-10">Find your business to see what it may and may not sell.</p>
        <SalesGuide />
      </section>}

      {tab === "alerts" && <section className="mx-auto max-w-5xl px-6 sm:px-10 py-14">
        <BusinessAlerts />
      </section>}

      {tab === "rules" && <section className="mx-auto max-w-5xl px-6 sm:px-10 py-14">
        <BusinessRules />
      </section>}

      {tab === "map" && <BusinessMap />}
    </>
  );
}
